import React, { useMemo } from "react";
import { useChallenge } from "@/context/ChallengeContext";
import { PageHeader, GoalBar, Stat, DisclaimerFooter } from "@/components/Bits";
import SessionCard from "@/components/SessionCard";
import CapitalChart from "@/components/CapitalChart";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtUSD, fmtPct, fmtDatePL, CHECKLIST_ITEMS, SESSION_META, STATUS_META } from "@/lib/calc";
import { TrendingUp, TrendingDown, Minus, Flame } from "lucide-react";

const appliesResult = (s) => ["profit", "loss", "be"].includes(s.status);
const resultOf = (s) => (s.status === "be" ? 0 : Number(s.result_usd) || 0);

export default function Dashboard() {
  const { data, loading, saveChecklist } = useChallenge();

  const active = useMemo(() => {
    if (!data) return null;
    const d = data.days[data.current_day - 1];
    return d;
  }, [data]);

  if (loading || !data || !active) {
    return <div className="text-demon-muted font-mono py-20 text-center">Ładowanie panelu...</div>;
  }

  const cfg = data.config;
  const balance = data.current_balance;
  const startBal = cfg.starting_balance;
  const profit = balance - startBal;
  const profitPct = (profit / startBal) * 100;
  const daysLeft = cfg.total_days - data.current_day;
  const perTurn = cfg.per_turn_2;

  // capital before each session within active day
  const capBefore = [];
  let run = active.opening_balance;
  active.sessions.forEach((s, i) => {
    capBefore[i] = run;
    if (appliesResult(s)) run += resultOf(s);
  });

  // today summary
  const nextIdx = active.sessions.findIndex((s) => s.status === "not_started" || s.status === "in_progress");
  const currentTura = nextIdx === -1 ? 3 : nextIdx + 1;
  const dayGoal = active.opening_balance * (Math.pow(1 + perTurn / 100, 3) - 1);
  const turaGoal = capBefore[Math.max(0, currentTura - 1)] * (perTurn / 100);
  const nextLabel = nextIdx === -1 ? "Dzień zamknięty" : SESSION_META[currentTura].sub;

  // plan status
  const planTarget = active.plan_2;
  let planState = { label: "ON PLAN", color: "text-amber-400", dot: "🟡" };
  if (balance > planTarget * 1.02) planState = { label: "ABOVE PLAN", color: "text-emerald-400", dot: "🟢" };
  else if (balance < planTarget * 0.98) planState = { label: "BELOW PLAN", color: "text-red-400", dot: "🔴" };

  const checklist = active.checklist || new Array(10).fill(false);
  const checkedCount = checklist.filter(Boolean).length;

  const toggleCheck = (i) => {
    const next = [...checklist];
    next[i] = !next[i];
    saveChecklist(active.day, next);
  };

  const progressPct = (data.current_day / cfg.total_days) * 100;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        right={
          <div className="text-right">
            <div className="font-mono text-2xl font-bold text-glow">
              Dzień {data.current_day} / {cfg.total_days}
            </div>
            <div className="text-sm text-demon-muted">
              Pozostało: {daysLeft} dni · {fmtDatePL(active.date)}
            </div>
          </div>
        }
      />

      {/* day progress bar */}
      <div className="glow-card rounded-2xl p-5 mb-6">
        <div className="flex justify-between text-xs text-demon-muted mb-2">
          <span>Start 01.10.2026</span>
          <span className="font-mono text-violet-300">{progressPct.toFixed(1)}%</span>
          <span>Koniec 31.12.2026</span>
        </div>
        <div className="h-3 rounded-full bg-black/60 border border-violet-900/40 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${progressPct}%`,
              background: "linear-gradient(90deg,#6d28d9,#a855f7,#c084fc)",
              boxShadow: "0 0 12px rgba(168,85,247,0.5)",
            }}
          />
        </div>
      </div>

      {/* TODAY mode */}
      <div className="glow-card rounded-2xl p-6 mb-6" data-testid="today-panel">
        <div className="flex items-center gap-2 mb-4">
          <Flame className="text-violet-400" size={20} />
          <h2 className="font-display font-bold text-xl tracking-wide">DZISIAJ</h2>
          <span className={`ml-auto text-sm font-semibold ${planState.color}`}>
            {planState.dot} {planState.label}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 font-mono">
          <TodayCell label="Kapitał" value={fmtUSD(balance)} accent="violet" />
          <TodayCell label="Cel dnia" value={"+" + fmtUSD(dayGoal)} accent="green" />
          <TodayCell label="Cel tury" value={"+" + fmtUSD(turaGoal)} accent="green" />
          <TodayCell label="Tura" value={`${currentTura} / 3`} />
          <TodayCell
            label="Status tury"
            value={STATUS_META[active.sessions[currentTura - 1]?.status]?.label || "—"}
          />
          <TodayCell label="Następna tura" value={nextLabel} />
        </div>
      </div>

      {/* Capital + goals */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="glow-card rounded-2xl p-6 lg:col-span-1" data-testid="current-capital-card">
          <div className="text-[11px] tracking-widest text-demon-muted">AKTUALNY KAPITAŁ</div>
          <div className="font-mono text-4xl sm:text-5xl font-extrabold text-glow mt-1" data-testid="dashboard-balance">
            {fmtUSD(balance)}
          </div>
          <div className="mt-4 space-y-1.5 font-mono text-sm">
            <Row label="Kapitał początkowy" value={fmtUSD(startBal)} />
            <Row label="Aktualny profit" value={(profit >= 0 ? "+" : "") + fmtUSD(profit)} accent={profit >= 0 ? "green" : "red"} />
            <Row label="Profit %" value={fmtPct(profitPct)} accent={profit >= 0 ? "green" : "red"} />
          </div>
        </div>

        <div className="glow-card rounded-2xl p-6 lg:col-span-2 flex flex-col justify-center gap-4">
          <div className="text-[11px] tracking-widest text-demon-muted">CEL · $350 → $200,000</div>
          <GoalBar label="POSTĘP DO $200,000" current={balance} target={cfg.goal_2} color="gold" testid="goal-progress-bar-200k" />
        </div>
      </div>

      {/* Three sessions */}
      <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
        Trzy tury · Dzień {data.current_day}
      </h2>
      <div className="grid lg:grid-cols-3 gap-5 mb-8">
        {active.sessions.map((s, i) => (
          <SessionCard
            key={s.session}
            day={active.day}
            session={s.session}
            sessionData={s}
            capitalBefore={capBefore[i]}
            targetPct={perTurn}
          />
        ))}
      </div>

      {/* Chart + checklist */}
      <div className="grid lg:grid-cols-2 gap-6 mb-2">
        <CapitalChart days={data.days} goal={2} />

        <div className="glow-card rounded-2xl p-6" data-testid="daily-checklist">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg">Daily Checklist</h3>
            <span className="font-mono text-sm text-violet-300">{checkedCount} / 10</span>
          </div>
          <div className="space-y-2">
            {CHECKLIST_ITEMS.map((item, i) => (
              <label
                key={i}
                className="flex items-center gap-3 text-sm cursor-pointer py-1.5 hover:text-white transition"
              >
                <Checkbox
                  data-testid={`checklist-item-${i}`}
                  checked={!!checklist[i]}
                  onCheckedChange={() => toggleCheck(i)}
                  className="border-violet-500/50 data-[state=checked]:bg-violet-600"
                />
                <span className={checklist[i] ? "text-demon-muted line-through" : "text-demon-silver"}>{item}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <DisclaimerFooter />
    </div>
  );
}

function TodayCell({ label, value, accent }) {
  const color =
    accent === "green" ? "text-emerald-400" : accent === "violet" ? "text-violet-300 text-glow" : "text-white";
  return (
    <div className="rounded-xl bg-black/40 border border-violet-900/30 p-3">
      <div className="text-[9px] tracking-widest text-demon-muted">{label}</div>
      <div className={`text-sm sm:text-base font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, accent }) {
  const color = accent === "green" ? "text-emerald-400" : accent === "red" ? "text-red-400" : "text-white";
  return (
    <div className="flex justify-between items-center">
      <span className="text-demon-muted text-xs">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}
